import { useCallback, useEffect } from 'react'
import { useRouter } from 'next/compat/router'
import { mergeRight, not, omit, path, pipe, whereEq } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'

import { getSearch, pushSearch } from '@/utils/location'

export const EMPTY_FILTERS = {
  housing_types: [],
  unit_types: [],
  unit_subtypes: [],
  min_price: null,
  max_price: null,
  pet_policy: [],
  bedroom_count: [],
  bathroom_count: [],
  availability_date: null,
  custom_availability_date: null,
  sort: null,
  search_only_verified: null,
  min_size: null,
  lease_type: null,
  rental_type: [],
  has_3d_tour: null,
  features: [],
  utilities: [],
  deals: []
}

export const useFilters = () => {
  const filters = useSelector(path(['listings', 'filters']))
  const router = useRouter()
  const isCompanyPage = router.pathname === '/company/[id]'

  const setFilter = useCallback(
    newFilter => {
      pushSearch(router, newFilter)
      if (!isCompanyPage) {
        window.scrollTo({ top: 0, behavior: 'auto' })
      }
    },
    [router, isCompanyPage]
  )

  const clearFilters = () => {
    setFilter(EMPTY_FILTERS)
  }

  const isFilterSet = pipe(
    omit(['cities', 'featured', 'page', 'page_size', 'neighborhoods', 'sort']),
    mergeRight(EMPTY_FILTERS),
    whereEq(EMPTY_FILTERS),
    not
  )(filters)

  return [filters, setFilter, clearFilters, isFilterSet]
}

export const useFiltersListener = ({ setDefaultCity = true } = {}) => {
  const dispatch = useDispatch()
  const router = useRouter()

  useEffect(() => {
    try {
      const search = getSearch(router, { setDefaultCity })
      dispatch.listings.updateFilter(search)
    } catch (error) {
      console.error('Error in getSearch:', error)
    }
  }, [dispatch.listings, router, setDefaultCity])
}

export const useCompanyFiltersListener = () => {
  const dispatch = useDispatch()
  const router = useRouter()

  useEffect(() => {
    try {
      const search = getSearch(router, { setDefaultCity: false })
      dispatch.listings.updateCompanyFilter(search)
    } catch (error) {
      console.error('Error in getSearch:', error)
    }
  }, [dispatch.listings, router])
}
